import React from "react";
import axios from "axios";
import StatementOfPurposeModal from "../components/StatementOfPurposeModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#1C4E80";
const SKY = "#0091D5";
const CORAL = "#EA6A47";
const SLATE = "#5A6B75";
const SKY_WASH = "#F2F9FD";
export const PENDING_SUBSCRIBE_KEY = "ureserve.pendingSubscribe";

const STATUS_MSG = {
  used: "This invitation has already been used.",
  cancelled: "This invitation has been cancelled.",
  expired: "This invitation has expired. Ask for a new one.",
};

export function pendingSubscribeToken() {
  const h = window.location.hash || "";
  if (h.startsWith("#subscribe?")) {
    const t = new URLSearchParams(h.slice(h.indexOf("?") + 1)).get("token");
    if (t) {
      sessionStorage.setItem(PENDING_SUBSCRIBE_KEY, t);
      return t;
    }
  }
  return sessionStorage.getItem(PENDING_SUBSCRIBE_KEY);
}

function leave(hash) {
  sessionStorage.removeItem(PENDING_SUBSCRIBE_KEY);
  window.location.hash = hash;
  window.location.reload();
}

function Card({ children, testId }) {
  return (
    <div className="min-h-screen px-5 py-10" style={{ background: SKY_WASH, color: NAVY, fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}>
      <div data-testid={testId} style={{ maxWidth: 540, margin: "0 auto", background: "#fff", border: "1px solid #E2E8EC", borderRadius: 8, padding: "26px 28px", boxShadow: "0 24px 60px rgba(28,78,128,0.10)" }}>
        {children}
      </div>
    </div>
  );
}

const pill = (bg, fg, border) => ({
  padding: "10px 22px", fontSize: 14, fontWeight: 700, color: fg, background: bg,
  border: border || "none", borderRadius: 999, cursor: "pointer",
});

function SubscribeSuccess({ user }) {
  return (
    <Card testId="subscribe-success-screen">
      <div style={{ fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 700, color: CORAL, marginBottom: 6 }}>Payment received</div>
      <h1 style={{ fontSize: 24, fontWeight: 800, margin: "0 0 10px 0" }}>Thank you — your subscription is active</h1>
      <p style={{ fontSize: 14, color: SLATE, lineHeight: 1.6, margin: "0 0 20px 0" }}>
        Stripe has confirmed your payment. Your association's subscription is being linked now; it can take a minute to show in the app.
      </p>
      <button type="button" data-testid="subscribe-success-continue" style={pill(CORAL, "#fff")} onClick={() => leave(user ? "#dashboard" : "#login")}>
        {user ? "Go to Dashboard" : "Sign in"}
      </button>
    </Card>
  );
}

export default function SubscribeInviteScreen({ user, onSignOut }) {
  const hash = window.location.hash || "";
  const [token] = React.useState(() => pendingSubscribeToken());
  const [invite, setInvite] = React.useState(null);
  const [error, setError] = React.useState("");
  const [ack, setAck] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [showSop, setShowSop] = React.useState(false);

  React.useEffect(() => {
    if (hash.startsWith("#subscribe/success")) return;
    if (!token) { setError("This invitation link is not valid."); return; }
    axios.get(`${API}/subscribe/invite/${encodeURIComponent(token)}`)
      .then(({ data }) => {
        if (data.kind === "new") {
          sessionStorage.removeItem(PENDING_SUBSCRIBE_KEY);
          window.location.hash = `#enroll?invite=${encodeURIComponent(token)}`;
          window.location.reload();
          return;
        }
        setInvite(data);
      })
      .catch((e) => setError(e?.response?.data?.detail || "This invitation link is not valid."));
  }, [token, hash]);

  if (hash.startsWith("#subscribe/success")) return <SubscribeSuccess user={user} />;

  const proceed = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/subscribe/invite/${encodeURIComponent(token)}/checkout`, {
        acknowledged: true, sop_acknowledged: true, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setShowSop(false);
      setBusy(false);
    }
  };

  const signIn = () => {
    window.location.hash = "#login";
    window.location.reload();
  };

  const wrongUser = user && invite && (user.email || "").toLowerCase() !== invite.email;
  const closed = invite && invite.status !== "pending";

  return (
    <Card testId="subscribe-invite-screen">
      <div style={{ fontSize: 11, letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 700, color: CORAL, marginBottom: 6 }}>Subscription invitation</div>
      {!invite && !error && <p style={{ color: SLATE, fontSize: 14 }}>Loading your invitation…</p>}
      {invite && (
        <>
          <h1 data-testid="subscribe-invite-title" style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.25, margin: "0 0 10px 0" }}>
            Subscribe {invite.association_name} to u<em>Reserve</em> Planning, LLC
          </h1>
          <div data-testid="subscribe-invite-plan" style={{ background: SKY_WASH, borderLeft: `3px solid ${SKY}`, padding: "10px 14px", fontSize: 14, marginBottom: 16 }}>
            <strong>{invite.plan_label} Plan</strong> · <strong>${invite.price_usd}/month</strong> · Cancel any time
          </div>
        </>
      )}
      {error && (
        <div data-testid="subscribe-invite-error" style={{ background: "#FDF5F2", border: `1px solid ${CORAL}`, color: CORAL, padding: "8px 12px", borderRadius: 4, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
          {error}
        </div>
      )}
      {closed && (
        <p data-testid="subscribe-invite-closed" style={{ color: CORAL, fontWeight: 600, fontSize: 14 }}>{STATUS_MSG[invite.status]}</p>
      )}
      {invite && !closed && !user && (
        <>
          <p style={{ fontSize: 14, color: SLATE, lineHeight: 1.6, margin: "0 0 18px 0" }}>
            This invitation was sent to <strong style={{ color: NAVY }}>{invite.email}</strong>. Please sign in with that account to continue.
          </p>
          <button type="button" data-testid="subscribe-invite-signin" style={pill(CORAL, "#fff")} onClick={signIn}>Sign in to continue →</button>
        </>
      )}
      {invite && !closed && wrongUser && (
        <>
          <p data-testid="subscribe-invite-wrong-user" style={{ fontSize: 14, color: SLATE, lineHeight: 1.6, margin: "0 0 18px 0" }}>
            You're signed in as <strong style={{ color: NAVY }}>{user.email}</strong>, but this invitation was sent to <strong style={{ color: NAVY }}>{invite.email}</strong>. Sign out and sign in with that account.
          </p>
          <button type="button" data-testid="subscribe-invite-signout" style={pill("#fff", NAVY, `1px solid ${NAVY}`)} onClick={onSignOut}>Sign out</button>
        </>
      )}
      {invite && !closed && user && !wrongUser && (
        <>
          <label data-testid="subscribe-invite-ack-label" style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, lineHeight: 1.5, margin: "0 0 18px 0", cursor: "pointer" }}>
            <input type="checkbox" data-testid="subscribe-invite-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ width: 17, height: 17, marginTop: 2, accentColor: NAVY }} />
            <span>{invite.ack_text}</span>
          </label>
          <button type="button" data-testid="subscribe-invite-continue" disabled={!ack || busy} onClick={() => { setError(""); setShowSop(true); }}
            style={{ ...pill(!ack || busy ? "#B9C3CC" : CORAL, "#fff"), cursor: !ack || busy ? "not-allowed" : "pointer", width: "100%" }}>
            {busy ? "Redirecting to Stripe…" : "Continue to Payment  →"}
          </button>
          <div style={{ fontSize: 11, color: SLATE, textAlign: "center", marginTop: 8 }}>Secure checkout by Stripe</div>
          <StatementOfPurposeModal
            open={showSop}
            acknowledgeLabel={busy ? "Redirecting to Stripe…" : "I Agree"}
            intro="Before starting your paid subscription, please review and acknowledge the Statement of Purpose of Use for uReserve"
            onAcknowledge={proceed}
            onCancel={() => leave("#dashboard")}
            busy={busy}
          />
        </>
      )}
      <div style={{ marginTop: 18, textAlign: "center" }}>
        <a data-testid="subscribe-invite-exit" onClick={() => leave(user ? "#dashboard" : "")} style={{ fontSize: 13, color: NAVY, cursor: "pointer", textDecoration: "underline", textDecorationColor: "#C9D6E2" }}>
          {user ? "Back to the app" : "Back to home"}
        </a>
      </div>
    </Card>
  );
}
